import React, { useEffect, useRef, useState } from 'react';

export const MAX_REFERENCE_BYTES = 10 * 1024 * 1024;

/**
 * THE POSTER — the prompt, before anything is drawn.
 *
 * The planner used to draw straight from a brief the host never saw, and the only recourse was
 * REDRAW and hope. Here the exact wording the image model will be given is on screen and
 * editable, and a reference picture can be attached for the drawing to follow.
 *
 * The prompt the server sends back is the host's own once they have edited it, otherwise the one
 * built from the plan's brief — so the box is never empty and RESET always has somewhere to go.
 * Local edits are kept while the field is dirty so a poll (every 3 s while the plan works)
 * cannot type over someone mid-sentence.
 */
export default function PosterStudio({
  plan, drawing, busy, onDraw, onSavePrompt, onUploadReference, onClearReference, onBlack = false,
}) {
  const serverPrompt = plan?.posterPrompt || '';
  const [prompt, setPrompt] = useState(serverPrompt);
  const [dirty, setDirty] = useState(false);
  const [fileError, setFileError] = useState(null);
  const fileInput = useRef(null);

  // Follow the server while the host is not mid-edit.
  useEffect(() => { if (!dirty) setPrompt(serverPrompt); }, [serverPrompt, dirty]);

  const disabled = !!(busy || drawing);
  const hasPoster = !!plan?.posterUrl;
  const ink = onBlack ? '#fff' : '#000';
  const dim = onBlack ? '#9CA3AF' : '#6B7280';
  const btn = `pl-btn${onBlack ? ' pl-on-black' : ''}`;

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                       // so re-picking the same file still fires
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFileError('THAT ISN’T AN IMAGE.'); return; }
    if (file.size > MAX_REFERENCE_BYTES) { setFileError('TOO BIG — 10 MB IS THE LIMIT.'); return; }
    setFileError(null);
    onUploadReference(file);
  };

  const reset = async () => { setDirty(false); setPrompt(''); await onSavePrompt(''); };
  const draw = async () => { await onDraw(prompt.trim()); setDirty(false); };

  return (
    <div className="font-mono" style={{ border: `2px solid ${ink}`, background: onBlack ? '#000' : '#fff', color: ink }} data-testid="poster-studio">
      <div className="flex items-baseline justify-between" style={{ padding: '9px 12px', borderBottom: `2px solid ${ink}` }}>
        <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em' }}>THE POSTER</span>
        <span className="uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: dim }}>
          {drawing ? 'DRAWING…' : hasPoster ? 'DRAWN' : 'NOT DRAWN YET'}
        </span>
      </div>

      <div style={{ padding: 12 }}>
        {hasPoster && (
          <img src={plan.posterUrl} alt="Event poster" className="w-full" style={{ border: `2px solid ${ink}`, display: 'block', marginBottom: 12 }} />
        )}

        {/* The prompt */}
        <div className="flex items-baseline justify-between" style={{ marginBottom: 6 }}>
          <span className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: dim }}>WHAT TO DRAW</span>
          {(plan?.posterPromptIsCustom || dirty) && (
            <button type="button" onClick={reset} disabled={disabled} className="underline uppercase disabled:opacity-50"
              style={{ fontSize: 10, letterSpacing: '0.05em', color: dim }} data-testid="poster-prompt-reset">RESET</button>
          )}
        </div>
        <textarea
          value={prompt}
          onChange={(e) => { setDirty(true); setPrompt(e.target.value); }}
          onBlur={() => { if (dirty && prompt.trim() !== serverPrompt.trim()) { onSavePrompt(prompt.trim()); setDirty(false); } }}
          rows={5}
          maxLength={2000}
          disabled={disabled}
          aria-label="Poster prompt"
          data-testid="poster-prompt"
          className="pl-input resize-none"
          style={{ fontSize: 12, lineHeight: 1.45, letterSpacing: 0, color: '#1A1A1A' }}
        />
        <p className="uppercase" style={{ fontSize: 9, letterSpacing: '0.05em', color: dim, marginTop: 5 }}>
          THIS IS EXACTLY WHAT THE DRAWING IS GIVEN. EDIT IT BEFORE YOU DRAW.
        </p>

        {/* The reference picture */}
        <div className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: dim, margin: '14px 0 6px' }}>SOMETHING TO FOLLOW</div>
        {plan?.posterReferenceUrl ? (
          <div className="flex items-center" style={{ gap: 10 }}>
            <img src={plan.posterReferenceUrl} alt="Reference" style={{ width: 56, height: 56, objectFit: 'cover', border: `2px solid ${ink}`, flex: '0 0 auto' }} data-testid="poster-reference" />
            <span className="uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: dim, flex: 1, minWidth: 0 }}>THE DRAWING WILL FOLLOW THIS PICTURE.</span>
            <button type="button" onClick={onClearReference} disabled={disabled} className={`${btn} flex-none`} style={{ fontSize: 10, padding: '6px 10px' }} data-testid="poster-reference-remove">REMOVE</button>
          </div>
        ) : (
          <>
            <button type="button" onClick={() => fileInput.current?.click()} disabled={disabled} className={btn} style={{ fontSize: 11, padding: '9px 14px' }} data-testid="poster-reference-add">
              ADD A PICTURE
            </button>
            <p className="uppercase" style={{ fontSize: 9, letterSpacing: '0.05em', color: dim, marginTop: 5 }}>
              OPTIONAL. A PHOTO, A POSTER YOU LIKE, A COLOUR &mdash; THE DRAWING FOLLOWS IT.
            </p>
          </>
        )}
        <input ref={fileInput} type="file" accept="image/*" onChange={pickFile} className="hidden" aria-label="Reference image" />
        {fileError && <p className="uppercase" style={{ fontSize: 10, color: '#EF4444', marginTop: 6 }}>{fileError}</p>}

        <button type="button" onClick={draw} disabled={disabled || prompt.trim().length < 8}
          className={`${btn} ${onBlack ? '' : 'pl-primary'} w-full`} style={{ fontSize: 11, padding: '11px 14px', letterSpacing: '0.1em', marginTop: 14 }} data-testid="poster-draw">
          {drawing ? 'DRAWING…' : hasPoster ? 'REDRAW THE POSTER' : 'DRAW THE POSTER'}
        </button>
      </div>
    </div>
  );
}
